import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Sun & Moon Direction — computes sun & moon azimuth + altitude given
// the user's location and the current time. Astronomy formulas adapted
// from standard NOAA / Meeus algorithms (Meeus, "Astronomical Algorithms"
// ch. 25, 47). Accuracy is ~0.01° for the sun and ~0.5° for the moon,
// well within phone-compass precision.
//
// Optional overlay: device orientation showing the user's current
// heading, so the sun/moon position appears relative to where the user
// is pointing.

const deg2rad = (d) => (d * Math.PI) / 180;
const rad2deg = (r) => (r * 180) / Math.PI;

// Julian day from JS Date
const julianDay = (date) => date.getTime() / 86400000 - 0.5 + 2440588;
// Days since J2000.0
const daysJ2000 = (date) => julianDay(date) - 2451545.0;

// Sun position (azimuth, altitude) at given lat/lng/date
function sunPosition(date, lat, lng) {
  const n = daysJ2000(date);
  const L = (280.460 + 0.9856474 * n) % 360; // mean longitude
  const g = deg2rad((357.528 + 0.9856003 * n) % 360); // mean anomaly
  // Ecliptic longitude
  const lambda = deg2rad((L + 1.915 * Math.sin(g) + 0.020 * Math.sin(2 * g)) % 360);
  // Obliquity of ecliptic
  const epsilon = deg2rad(23.439 - 0.0000004 * n);
  // Equatorial coords
  const ra = Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda));
  const dec = Math.asin(Math.sin(epsilon) * Math.sin(lambda));
  // Local sidereal time
  const gmst = (18.697374558 + 24.06570982441908 * n) % 24;
  const lst = (gmst + lng / 15) * 15; // degrees
  const ha = deg2rad(((lst - rad2deg(ra)) + 540) % 360 - 180);
  const phi = deg2rad(lat);
  const altitude = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(ha));
  let azimuth = Math.atan2(Math.sin(ha), Math.cos(ha) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
  azimuth = (rad2deg(azimuth) + 180) % 360; // measured from N, clockwise
  return { azimuth, altitude: rad2deg(altitude) };
}

// Moon position — simplified low-precision algorithm (Meeus, ch. 47
// summary form). Accuracy ~0.5° in position, which suffices for
// "approximate direction" use cases.
function moonPosition(date, lat, lng) {
  const n = daysJ2000(date);
  // Mean longitude, mean anomaly, mean argument of latitude
  const L = deg2rad((218.316 + 13.176396 * n) % 360);
  const M = deg2rad((134.963 + 13.064993 * n) % 360);
  const F = deg2rad((93.272 + 13.229350 * n) % 360);
  // Ecliptic longitude and latitude (low precision)
  const lambda = L + deg2rad(6.289 * Math.sin(M));
  const beta = deg2rad(5.128 * Math.sin(F));
  const epsilon = deg2rad(23.439 - 0.0000004 * n);
  // Convert to equatorial
  const ra = Math.atan2(Math.sin(lambda) * Math.cos(epsilon) - Math.tan(beta) * Math.sin(epsilon), Math.cos(lambda));
  const dec = Math.asin(Math.sin(beta) * Math.cos(epsilon) + Math.cos(beta) * Math.sin(epsilon) * Math.sin(lambda));
  // Local sidereal time
  const gmst = (18.697374558 + 24.06570982441908 * n) % 24;
  const lst = (gmst + lng / 15) * 15;
  const ha = deg2rad(((lst - rad2deg(ra)) + 540) % 360 - 180);
  const phi = deg2rad(lat);
  const altitude = Math.asin(Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(ha));
  let azimuth = Math.atan2(Math.sin(ha), Math.cos(ha) * Math.sin(phi) - Math.tan(dec) * Math.cos(phi));
  azimuth = (rad2deg(azimuth) + 180) % 360;
  return { azimuth, altitude: rad2deg(altitude) };
}

// Moon phase fraction illuminated (0 = new, 1 = full)
function moonPhase(date) {
  const n = daysJ2000(date);
  const D = deg2rad((297.850 + 12.190749 * n) % 360);
  const M = deg2rad((357.528 + 0.9856003 * n) % 360);
  const Mm = deg2rad((134.963 + 13.064993 * n) % 360);
  // Phase angle of the moon
  const i = deg2rad(180) - D - deg2rad(6.289 * Math.sin(Mm)) + deg2rad(2.100 * Math.sin(M));
  return (1 + Math.cos(i)) / 2;
}

const phaseName = (p, dayOfMoon) => {
  // We use illuminated fraction + crude waxing/waning sign from day-of-month
  if (p < 0.03) return "New";
  if (p < 0.48) return "Crescent";
  if (p < 0.52) return "Quarter";
  if (p < 0.97) return "Gibbous";
  return "Full";
};

const compassDir = (az) => {
  const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
  return dirs[Math.round(az / 22.5) % 16];
};

export default function SunMoonDirection() {
  const [locState, setLocState] = useState("idle");
  const [coords, setCoords] = useState(null);
  const [now, setNow] = useState(new Date());
  const [heading, setHeading] = useState(null);
  const [orientState, setOrientState] = useState("idle");
  const handlerRef = useRef(null);

  // Tick the clock every minute (positions change slowly)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const enableLocation = () => {
    if (!navigator.geolocation) { setLocState("unsupported"); return; }
    setLocState("requesting");
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); setLocState("ok"); },
      () => setLocState("denied"),
      { enableHighAccuracy: false, timeout: 15000 }
    );
  };

  const enableOrientation = async () => {
    if (typeof DeviceOrientationEvent === "undefined") { setOrientState("unsupported"); return; }
    try {
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        const r = await DeviceOrientationEvent.requestPermission();
        if (r !== "granted") { setOrientState("denied"); return; }
      }
      handlerRef.current = (e) => {
        let h;
        if (typeof e.webkitCompassHeading === "number") h = e.webkitCompassHeading;
        else if (typeof e.alpha === "number") h = (360 - e.alpha) % 360;
        else return;
        setHeading(h);
      };
      window.addEventListener("deviceorientation", handlerRef.current, true);
      setOrientState("ok");
    } catch {
      setOrientState("denied");
    }
  };

  useEffect(() => () => {
    if (handlerRef.current) window.removeEventListener("deviceorientation", handlerRef.current, true);
  }, []);

  const sun = coords ? sunPosition(now, coords.lat, coords.lng) : null;
  const moon = coords ? moonPosition(now, coords.lat, coords.lng) : null;
  const phase = moonPhase(now);

  // Rotation of the compass rose so that current heading is at top
  const rotateRose = heading != null ? -heading : 0;

  return (
    <ToolsShell title="Sun & Moon">
      {locState !== "ok" ? (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Find where the sun and moon are in the sky from your location, right now. Useful for photographers (golden hour direction), gardeners, hikers.
          </p>
          <button onClick={enableLocation} style={primaryBtn} disabled={locState === "requesting"}>
            {locState === "requesting" ? "Getting location…" : "Enable location"}
          </button>
          <SensorNotice state={locState === "idle" || locState === "requesting" ? "prompt" : locState} what="Location" />
        </div>
      ) : (
        <>
          {/* Optional orientation toggle */}
          {orientState !== "ok" ? (
            <div style={{ marginBottom: 14, padding: "12px 14px", background: "#13141F", border: "1px solid #262839", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ fontSize: 13, color: "#C8CADF" }}>Add compass for live "point at the sun" overlay?</div>
              <button onClick={enableOrientation} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                Enable
              </button>
            </div>
          ) : null}

          {/* Compass rose with sun/moon markers */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
            <svg width="280" height="280" viewBox="0 0 280 280">
              {/* Outer ring */}
              <circle cx="140" cy="140" r="120" fill="#13141F" stroke="#262839" strokeWidth="2" />
              {/* Compass rotates with heading */}
              <g transform={`rotate(${rotateRose} 140 140)`}>
                {/* Cardinal letters */}
                {["N", "E", "S", "W"].map((c, i) => (
                  <text
                    key={c}
                    x={140 + 100 * Math.sin((i * Math.PI) / 2)}
                    y={140 - 100 * Math.cos((i * Math.PI) / 2) + 5}
                    textAnchor="middle" fontSize="16" fontWeight="700"
                    fill={c === "N" ? TOOLS_ACCENT : "#5B5E7A"}
                  >{c}</text>
                ))}
                {/* Tick marks */}
                {Array.from({ length: 36 }).map((_, i) => (
                  <line
                    key={i}
                    x1={140 + 110 * Math.sin((i * 10 * Math.PI) / 180)}
                    y1={140 - 110 * Math.cos((i * 10 * Math.PI) / 180)}
                    x2={140 + 118 * Math.sin((i * 10 * Math.PI) / 180)}
                    y2={140 - 118 * Math.cos((i * 10 * Math.PI) / 180)}
                    stroke={i % 9 === 0 ? "#7C8099" : "#262839"}
                    strokeWidth={i % 9 === 0 ? 2 : 1}
                  />
                ))}
                {/* Sun marker */}
                {sun && sun.altitude > 0 && (
                  <g transform={`rotate(${sun.azimuth} 140 140)`}>
                    <line x1="140" y1="140" x2="140" y2="40" stroke="#F8B84E" strokeWidth="2" />
                    <circle cx="140" cy="40" r="14" fill="#F8B84E" />
                    <text x="140" y="44" textAnchor="middle" fontSize="14" fontWeight="700" fill="#0A0B14">☀</text>
                  </g>
                )}
                {sun && sun.altitude <= 0 && (
                  <g transform={`rotate(${sun.azimuth} 140 140)`}>
                    <line x1="140" y1="140" x2="140" y2="65" stroke="#F8B84E55" strokeWidth="2" strokeDasharray="3 3" />
                    <circle cx="140" cy="65" r="10" fill="none" stroke="#F8B84E" strokeWidth="2" />
                  </g>
                )}
                {/* Moon marker */}
                {moon && (
                  <g transform={`rotate(${moon.azimuth} 140 140)`}>
                    {moon.altitude > 0 ? (
                      <>
                        <line x1="140" y1="140" x2="140" y2="70" stroke="#9AB8D9" strokeWidth="2" />
                        <circle cx="140" cy="70" r="11" fill="#9AB8D9" />
                      </>
                    ) : (
                      <>
                        <line x1="140" y1="140" x2="140" y2="90" stroke="#9AB8D955" strokeWidth="2" strokeDasharray="3 3" />
                        <circle cx="140" cy="90" r="8" fill="none" stroke="#9AB8D9" strokeWidth="2" />
                      </>
                    )}
                  </g>
                )}
              </g>
              {/* Center dot + heading indicator */}
              <circle cx="140" cy="140" r="4" fill={TOOLS_ACCENT} />
              {heading != null && (
                <polygon points="140,20 134,32 146,32" fill={TOOLS_ACCENT} />
              )}
            </svg>
          </div>

          {/* Readouts */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 16 }}>
            <Card label="Sun" color="#F8B84E" body={sun} />
            <Card label="Moon" color="#9AB8D9" body={moon} extra={`${(phase * 100).toFixed(0)}% lit · ${phaseName(phase)}`} />
          </div>

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
            Solid markers = above horizon; dashed = below. Bearings measured clockwise from north. Astronomy ~0.5° accuracy.
          </p>
        </>
      )}
    </ToolsShell>
  );
}

function Card({ label, color, body, extra }) {
  if (!body) return null;
  const aboveHorizon = body.altitude > 0;
  return (
    <div style={{ background: "#13141F", border: `1px solid ${color}55`, borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ fontSize: 12, color: "#7C8099", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color, fontVariantNumeric: "tabular-nums" }}>
        {body.azimuth.toFixed(0)}° {(() => {
          const dirs = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
          return dirs[Math.round(body.azimuth / 22.5) % 16];
        })()}
      </div>
      <div style={{ fontSize: 12, color: "#9A9DBE", marginTop: 2 }}>
        {aboveHorizon ? `${body.altitude.toFixed(0)}° above horizon` : `${(-body.altitude).toFixed(0)}° below horizon`}
      </div>
      {extra && <div style={{ fontSize: 11, color: "#7C8099", marginTop: 4 }}>{extra}</div>}
    </div>
  );
}
